/*********************************************************************************
 * Copyright (C) 2025 VECROS Technologies Pvt. Ltd. <legal@vecros.com>
 * Proprietary and confidential.
 **********************************************************************************/

import React from "react";
import { Box, Typography, useTheme, Tooltip } from "@mui/material";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import EditNoteRoundedIcon from "@mui/icons-material/EditNoteRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import OpenInFullRoundedIcon from "@mui/icons-material/OpenInFullRounded";
import CloseFullscreenRoundedIcon from "@mui/icons-material/CloseFullscreenRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { useAssistant } from "../AssistantContext";

/**
 * Title (click → conversation history) + actions: new chat, expand/collapse, close.
 */
export default function AssistantHeader({ title, onOpenHistory, onOpenSettings, centered }) {
  const theme = useTheme();
  const { newChat, toggleMode, closePanel, mode } = useAssistant();

  const iconBtn = {
    display: "inline-flex",
    p: 0.75,
    borderRadius: 1.5,
    cursor: "pointer",
    color: theme.palette.secondaryText.color,
    "&:hover": { backgroundColor: theme.palette.tertiary.main, color: theme.palette.text.color },
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        px: 1.5,
        py: 1.25,
        borderBottom: `1px solid ${theme.palette.border.color}`,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.25,
          width: centered ? "min(820px, 100%)" : "auto",
          mx: centered ? "auto" : 0,
          minWidth: 0,
        }}
      >
        <Tooltip title="Conversation history" arrow>
          <Box
            role="button"
            aria-label="Open conversation history"
            onClick={onOpenHistory}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.25,
              minWidth: 0,
              px: 0.75,
              py: 0.25,
              ml: -0.75,
              borderRadius: 1.5,
              cursor: "pointer",
              "&:hover": { backgroundColor: theme.palette.tertiary.main },
            }}
          >
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: 15,
                color: theme.palette.text.color,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {title || "AI Assistant"}
            </Typography>
            <KeyboardArrowDownRoundedIcon
              sx={{ fontSize: 20, color: theme.palette.secondaryText.color, flexShrink: 0 }}
            />
          </Box>
        </Tooltip>

        <Box sx={{ ml: "auto", display: "flex", gap: 0.25 }}>
          <Tooltip title="Assistant settings" arrow>
            <Box sx={iconBtn} onClick={onOpenSettings} role="button" aria-label="Assistant settings">
              <TuneRoundedIcon sx={{ fontSize: 18 }} />
            </Box>
          </Tooltip>
          <Tooltip title="New chat" arrow>
            <Box sx={iconBtn} onClick={newChat} role="button" aria-label="New chat">
              <EditNoteRoundedIcon sx={{ fontSize: 20 }} />
            </Box>
          </Tooltip>
          <Tooltip title={mode === "focus" ? "Collapse" : "Full screen"} arrow>
            <Box sx={iconBtn} onClick={toggleMode} role="button" aria-label="Toggle full screen">
              {mode === "focus" ? (
                <CloseFullscreenRoundedIcon sx={{ fontSize: 18 }} />
              ) : (
                <OpenInFullRoundedIcon sx={{ fontSize: 18 }} />
              )}
            </Box>
          </Tooltip>
          <Tooltip title="Close" arrow>
            <Box sx={iconBtn} onClick={closePanel} role="button" aria-label="Close assistant">
              <CloseRoundedIcon sx={{ fontSize: 20 }} />
            </Box>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
}
